/**
 * «Tareas parecidas» bajo el título de una tarea nueva, para no crear duplicados. Busca (con un pequeño
 * retardo, desde 3 letras) con la búsqueda por palabras de la API, incluidas las cerradas, y muestra
 * hasta 5 con su ID, título y estado o marca. Un clic abre esa tarea.
 */
import { useEffect, useState } from 'react';
import { formatearNumero } from '@cdtec-backlog/contrato';
import { MINIMO_PARECIDAS, useTareasParecidas } from './hooks';
import { EstadoTarea } from './MarcaCierre';
import './TareasParecidas.css';

/** Retardo entre la última tecla y la búsqueda. */
export const RETARDO_PARECIDAS_MS = 400;

interface Props {
  titulo: string;
  onAbrir: (id: number) => void;
}

export function TareasParecidas({ titulo, onAbrir }: Props) {
  const [buscado, setBuscado] = useState(titulo);
  useEffect(() => {
    const espera = setTimeout(() => setBuscado(titulo), RETARDO_PARECIDAS_MS);
    return () => clearTimeout(espera);
  }, [titulo]);

  const parecidas = useTareasParecidas(buscado);
  const lista = buscado.trim().length >= MINIMO_PARECIDAS ? (parecidas.data ?? []) : [];

  // Región viva siempre presente: el lector de pantalla anuncia cuando aparecen.
  return (
    <div className="parecidas-region" aria-live="polite">
      {lista.length > 0 && (
        <section className="parecidas" aria-label="Tareas parecidas">
          <p>
            <strong>Tareas parecidas.</strong> Revisa si ya existe antes de crear otra (incluye las
            cerradas).
          </p>
          <ul>
            {lista.map((tarea) => (
              <li key={tarea.id}>
                <button type="button" onClick={() => onAbrir(tarea.id)}>
                  <span className="parecidas__id">{formatearNumero(tarea.numero)}</span>
                  <span className="parecidas__titulo" title={tarea.titulo}>
                    {tarea.titulo}
                  </span>
                  <EstadoTarea estado={tarea.estado} />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
